<template>
  <div class="overflow-hidden relative">
    <div class="absolute inset-0" aria-hidden="true">
      <nuxt-img
        :src="bgImageSrc"
        alt="background"
        class="object-cover w-full h-full"
        width="1920"
        height="1415"
        format="webp"
        loading="eager"
        fetchpriority="high"
        preload
        sizes="xs:330px sm:440px md:760px lg:1024px xl:1200px 2xl:1920px"
      />
    </div>

    <section class="px-4 mx-auto max-w-5xl xl:px-0 md:px-6">
      <div class="relative z-10">
        <h1 class="pt-6 text-3xl text-center sm:text-4xl lg:pt-12 xl:text-6xl md:text-5xl font-ww-bold">
          {{ mainTitle }}
        </h1>

        <p class="mt-3 text-xl italic text-center lg:mt-5 xl:text-3xl md:text-2xl font-ww-regular">
          {{ author }}
        </p>

        <div class="grid grid-cols-12 lg:flex">
          <div class="lg:basis-[585px] col-span-12">
            <div class="mt-4 text-base italic text-left sm:mt-8 lg:mt-16 xl:text-xl md:text-lg font-ww-regular lg:text-left">
              {{ descriptionPart1 }}
              <strong>{{ descriptionStrong }}</strong>
              <br /><br />
              {{ descriptionPart2 }}
            </div>

            <h2 class="mt-6 xl:text-27 md:text-2xl text-xl leading-7 font-ww-bold text-[#4B3D2C] lg:text-left text-left">
              {{ subheading }}
            </h2>

            <div class="flex justify-center pb-6 lg:pb-20 lg:justify-start">
              <button
              @click="scrollTo(scrollTarget, scrollPosition(mobilePosition))"
                type="button"
                :style="{ background: primaryColor }"
                class="text-white bnt-basic items-center mt-8 flex xl:!py-5 font-ww-bold font-bold !text-lg md:!py-4 !py-3 gap-x-2 !capitalize md-max:px-8 md:!text-xl lg:!text-3xl !px-12"
                :aria-label="ctaText"
              >
                <span>{{ ctaText }}</span>
                <svg
                  width="28"
                  height="23"
                  viewBox="0 0 28 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="relative top-0.5 w-5 md:w-6 lg:top-1 lg:w-auto"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M27.3403 12.5758L17.9164 22.0853C17.787 22.2166 17.6327 22.321 17.4625 22.3922C17.2923 22.4634 17.1096 22.5 16.925 22.5C16.7405 22.5 16.5578 22.4634 16.3876 22.3922C16.2174 22.321 16.0631 22.2166 15.9337 22.0853C15.6706 21.8187 15.5231 21.4594 15.5231 21.0852C15.5231 20.7109 15.6706 20.3517 15.9337 20.0851L22.905 13.0496H1.90953C1.60041 13.0478 1.30464 12.9237 1.08722 12.7043C0.869793 12.4849 0.748502 12.1883 0.750014 11.8797V11.3841C0.748502 11.0755 0.869793 10.7789 1.08722 10.5596C1.30464 10.3402 1.60041 10.216 1.90953 10.2143H23.1608L15.9289 2.91494C15.6659 2.6483 15.5184 2.28907 15.5184 1.91482C15.5184 1.54056 15.6659 1.18133 15.9289 0.914689C16.0583 0.783352 16.2126 0.679029 16.3828 0.607828C16.553 0.536628 16.7358 0.5 16.9203 0.5C17.1048 0.5 17.2875 0.536628 17.4577 0.607828C17.6279 0.679029 17.7822 0.783352 17.9116 0.914689L27.3233 10.4119C27.5559 10.6493 27.6968 10.9612 27.7211 11.2924C27.7679 11.5211 27.7577 11.7578 27.6913 11.9816C27.6249 12.2054 27.5043 12.4095 27.3403 12.5758Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          </div>

          
          <div class="col-span-12">
            <nuxt-img 
              :src="heroImageSrc" 
              format="webp" 
              loading="eager" 
              fetchpriority="high" 
              sizes="xs:330px sm:420px md:624px lg:624px xl:624px 2xl:624px" 
              :alt="heroImageAlt" 
              width="624" 
              height="708" 
              preload
              class="bottom-0 mx-auto lg:-bottom-6 lg:absolute 2xl:-right-64 lg:-right-52" 
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
const props = defineProps({
  bgImageSrc: {
    type: String,
    default: "/assets/images/bg-hero-cognition.png"
  },
  mainTitle: {
    type: String,
    default: "\"My Family Has a History of Dementia… That's Why I Support My Brain Every Morning.\""
  },
  author: {
    type: String,
    default: "- Dr Charles Livingston"
  },
  descriptionPart1: {
    type: String,
    default: "My grandmother struggled with dementia… and watching her fade was one of the hardest things I've ever seen. Now that I'm in my 40s, I can't help but wonder — will that happen to me too?"
  },
  descriptionStrong: {
    type: String,
    default: "That fear made me rethink every choice I make, especially the things I put into my body every single day."
  },
  descriptionPart2: {
    type: String,
    default: "That's why I turned to mushroom coffee: the same rich, smooth taste I love, but with Lion's Mane and Chaga — natural ingredients studied for their ability to support focus, memory, and long-term brain health."
  },
  subheading: {
    type: String,
    default: "Enjoy your daily cup of coffee — while giving your brain the support it needs for the years ahead."
  },
  ctaText: {
    type: String,
    default: "Try It Risk-Free Today"
  },
  primaryColor: {
    type: String,
    default: "#ff9315"
  },
  heroImageSrc: {
    type: String,
    default: "/assets/images/right-hero-sec2-new.png"
  },
  heroImageAlt: {
    type: String,
    default: "Lifeboost Mushroom Coffee product with brain health benefits"
  },
  scrollTarget: {
    type: String,
    default: "the-products"
  },
  mobilePosition: {
    type: String,
    default: "bottom"
  }
})

defineBuilderComponent({
  friendlyName: "Hero - Title Info Image CTA",
  inputs: [
    {
      name: "bgImageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/bg-hero-cognition.png"
    },
    {
      name: "mainTitle",
      type: "longText",
      defaultValue: "\"My Family Has a History of Dementia… That's Why I Support My Brain Every Morning.\""
    },
    {
      name: "author",
      type: "text",
      defaultValue: "- Dr Charles Livingston"
    },
    {
      name: "descriptionPart1",
      type: "longText",
      defaultValue: "My grandmother struggled with dementia… and watching her fade was one of the hardest things I've ever seen. Now that I'm in my 40s, I can't help but wonder — will that happen to me too?"
    },
    {
      name: "descriptionStrong",
      type: "longText",
      defaultValue: "That fear made me rethink every choice I make, especially the things I put into my body every single day."
    },
    {
      name: "descriptionPart2",
      type: "longText",
      defaultValue: "That's why I turned to mushroom coffee: the same rich, smooth taste I love, but with Lion's Mane and Chaga — natural ingredients studied for their ability to support focus, memory, and long-term brain health."
    },
    {
      name: "subheading",
      type: "longText",
      defaultValue: "Enjoy your daily cup of coffee — while giving your brain the support it needs for the years ahead."
    },
    {
      name: "ctaText",
      type: "text",
      defaultValue: "Try It Risk-Free Today"
    },
    {
      name: "primaryColor",
      type: "color",
      defaultValue: "#ff9315"
    },
    {
      name: "heroImageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/right-hero-sec2-new.png"
    },
    {
      name: "heroImageAlt",
      type: "text",
      defaultValue: "Lifeboost Mushroom Coffee product with brain health benefits"
    },
    {
      name: "scrollTarget",
      type: "text",
      defaultValue: "the-products"
    },
    {
      name: "mobilePosition",
      type: "enum",
      friendlyName: "Scroll Position Mobile",
      enum: [
        { label: "Bottom", value: "bottom" },
        { label: "Top", value: "top" }
      ],
      defaultValue: "bottom"
    }
  ]
})
</script>
